import * as THREE from 'three';

/**
 * LabelManager2D - A lightweight class to manage 2D HTML-based player name labels
 * Uses DOM elements positioned over the 3D scene for efficient player name display
 */
export class LabelManager2D {
  private camera: THREE.Camera;
  private labels: Map<string, HTMLElement> = new Map();
  private labelsContainer: HTMLElement;
  private colors: string[] = ['#ff5a5a', '#3498ff', '#6ebf4b', '#ffe234', '#ff9e2d']; // Same as 3D version
  
  // Track camera movement to avoid unnecessary updates
  private lastCameraPosition: THREE.Vector3 = new THREE.Vector3();
  private lastCameraRotation: THREE.Euler = new THREE.Euler();
  private updateThreshold: number = 0.05; // Smaller threshold for smoother updates
  
  constructor(camera: THREE.Camera) {
    this.camera = camera;
    
    // Get the labels container
    const container = document.getElementById('player-labels');
    if (!container) {
      throw new Error('Player labels container not found in the DOM');
    }
    this.labelsContainer = container;
    
    // Initialize camera tracking
    this.lastCameraPosition.copy(camera.position);
    this.lastCameraRotation.copy(camera.rotation);
  }
  
  /**
   * Create a label for a player
   */
  public createLabel(playerId: string, name: string, position: THREE.Vector3): void {
    // Remove existing label if any
    this.removeLabel(playerId);
    
    // If name is empty, don't create a label
    if (!name || name.trim() === '') {
      return;
    }
    
    // Check if on mobile
    const isMobile = window.innerWidth <= 768;
    
    // Create the label element
    const label = document.createElement('div');
    label.className = 'player-label';
    label.id = `label-${playerId}`;
    
    // Choose a color based on the player ID for consistent coloring
    const colorIndex = this.hashStringToNumber(playerId) % this.colors.length;
    label.style.color = this.colors[colorIndex];
    
    // Apply mobile-specific styles directly
    if (isMobile) {
      label.style.fontSize = '12px';
      label.style.padding = '3px 6px';
    } else {
      label.style.fontSize = '14px';
      label.style.padding = '4px 8px';
    }
    
    // Set the label text
    label.textContent = name;
    
    // Add to the container
    this.labelsContainer.appendChild(label);
    
    // Store reference to the label
    this.labels.set(playerId, label);
    
    // Initial position update
    this.updateLabelPosition(playerId, position);
  }
  
  /**
   * Update a label's position based on the player's 3D position
   */
  public updateLabel(playerId: string, position: THREE.Vector3): void {
    this.updateLabelPosition(playerId, position);
  }
  
  /**
   * Update all labels (call this in the animation loop)
   */
  public update(): void {
    // Only update all labels if camera has moved significantly
    if (this.hasCameraMovedSignificantly()) {
      this.updateAllLabelPositions();
      
      // Update last known camera position and rotation
      this.lastCameraPosition.copy(this.camera.position);
      this.lastCameraRotation.copy(this.camera.rotation);
    }
  }
  
  /**
   * Check if camera has moved enough to warrant updating all labels
   */
  private hasCameraMovedSignificantly(): boolean {
    // Check position change
    const positionChanged = this.camera.position.distanceTo(this.lastCameraPosition) > this.updateThreshold;
    
    // Check rotation change (more sensitive for better responsiveness)
    const rotationChanged = 
      Math.abs(this.camera.rotation.x - this.lastCameraRotation.x) > 0.005 || 
      Math.abs(this.camera.rotation.y - this.lastCameraRotation.y) > 0.005 || 
      Math.abs(this.camera.rotation.z - this.lastCameraRotation.z) > 0.005;
    
    return positionChanged || rotationChanged;
  }
  
  /**
   * Update positions of all labels
   */
  public updateAllLabelPositions(): void {
    this.labels.forEach((label, playerId) => {
      const position = this.getPlayerPosition(playerId);
      if (position) {
        this.updateLabelPosition(playerId, position);
      }
    });
  }
  
  /**
   * Get player position from label data
   */
  private getPlayerPosition(playerId: string): THREE.Vector3 | null {
    const label = this.labels.get(playerId);
    if (!label || !label.dataset.position) return null;
    
    try {
      const position = JSON.parse(label.dataset.position);
      return new THREE.Vector3(position.x, position.y, position.z);
    } catch (e) {
      return null;
    }
  }
  
  /**
   * Helper to convert world position to screen position
   */
  private updateLabelPosition(playerId: string, position: THREE.Vector3): void {
    const label = this.labels.get(playerId);
    if (!label) return;
    
    // Store the position for later use
    label.dataset.position = JSON.stringify({ x: position.x, y: position.y, z: position.z });
    
    // Clone the position and add height offset (above the player)
    // Pour les autres joueurs (ni joueur principal ni police), utiliser une hauteur plus basse (1.0 au lieu de 1.5)
    const mainPlayerId = window.localStorage.getItem('playerId') || '';
    const isMainPlayer = playerId === mainPlayerId;
    const isPolice = playerId.startsWith('police-');
    const heightOffset = (isMainPlayer || isPolice) ? 1.5 : 1.0;
    
    const offsetPosition = position.clone().add(new THREE.Vector3(0, heightOffset, 0));
    
    // Project the 3D position to 2D screen space
    const screenPosition = this.worldToScreen(offsetPosition);
    
    // Only show if the position is in front of the camera
    if (screenPosition.z > 0) {
      label.style.display = 'block';
      label.style.left = `${screenPosition.x}px`;
      label.style.top = `${screenPosition.y}px`;
      
      // Scale based on distance
      const distance = this.camera.position.distanceTo(position);
      
      // Hide if too far
      if (distance > 50) {
        label.style.display = 'none';
      } else {
        // Adjust opacity based on distance - fade out as distance increases
        const opacity = Math.max(0.3, Math.min(1.0, 1.0 - (distance / 50)));
        label.style.opacity = opacity.toString();
      }
    } else {
      // Hide if behind camera
      label.style.display = 'none';
    }
  }
  
  /**
   * Convert a 3D world position to 2D screen coordinates
   */
  private worldToScreen(position: THREE.Vector3): THREE.Vector3 {
    // Clone position for the projection
    const pos = position.clone();
    
    // Project position to screen space
    pos.project(this.camera);
    
    // Convert normalized device coordinates (-1 to 1) to pixel coordinates
    const halfWidth = window.innerWidth / 2;
    const halfHeight = window.innerHeight / 2;
    const x = (pos.x * halfWidth) + halfWidth;
    const y = -(pos.y * halfHeight) + halfHeight;
    
    return new THREE.Vector3(x, y, pos.z);
  }
  
  /**
   * Remove a label
   */
  public removeLabel(playerId: string): void {
    const label = this.labels.get(playerId);
    if (label && label.parentNode) {
      label.parentNode.removeChild(label);
      this.labels.delete(playerId);
    }
  }
  
  /**
   * Update a player's name
   */
  public updateName(playerId: string, name: string, position: THREE.Vector3): void {
    const label = this.labels.get(playerId);
    if (label) {
      label.textContent = name;
    } else {
      this.createLabel(playerId, name, position);
    }
  }
  
  /**
   * Helper to hash a string to a number (for consistent color selection)
   */
  private hashStringToNumber(str: string): number {
    let hash = 0;
    if (str.length === 0) return hash;
    
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    
    return Math.abs(hash);
  }
} 